import { useState } from "react";
import { router } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import { Input, Checkbox } from "@/components/ui/Field";

export type AdminStage = {
  id: number;
  name: string;
  description: string | null;
  position: number;
  active: boolean;
  steps?: { id: number; title: string }[];
};

// Stages are the groups organizers work through in order. Managed inline rather
// than on their own page: there are only ever a handful, and they're most
// useful edited next to the steps that live in them.
export default function StagesPanel({ stages }: { stages: AdminStage[] }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  function add(e: React.FormEvent) {
    e.preventDefault();
    router.post(
      "/admin/stages",
      { name, description },
      {
        preserveScroll: true,
        onSuccess: () => {
          setName("");
          setDescription("");
        },
      },
    );
  }

  function move(stage: AdminStage, delta: number) {
    router.patch(
      `/admin/stages/${stage.id}`,
      { position: stage.position + delta },
      { preserveScroll: true },
    );
  }

  return (
    <Panel title={`stages.csv - ${stages.length}`}>
      <div className="flex flex-col gap-4">
        <p className="text-caption text-ink-muted">
          Organizers work through these in order. A stage stays locked until
          everything required before it is done.
        </p>

        {stages.length > 0 && (
          <div className="flex flex-col">
            {stages.map((stage, i) => (
              <div
                key={stage.id}
                className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0"
              >
                <span className="text-ink-soft font-display text-caption w-6 shrink-0">
                  {i + 1}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-body text-ink font-bold">{stage.name}</p>
                  {stage.description && (
                    <p className="text-caption text-ink-soft">
                      {stage.description}
                    </p>
                  )}
                </div>

                <Badge tone="neutral">
                  {stage.steps?.length ?? 0}{" "}
                  {(stage.steps?.length ?? 0) === 1 ? "step" : "steps"}
                </Badge>
                {!stage.active && <Badge tone="neutral">Hidden</Badge>}

                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={i === 0}
                    onClick={() => move(stage, -1)}
                    aria-label={`Move ${stage.name} up`}
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={i === stages.length - 1}
                    onClick={() => move(stage, 1)}
                    aria-label={`Move ${stage.name} down`}
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (
                        confirm(
                          `Remove "${stage.name}"? Its steps move to Everything else - nothing organizers have already done is lost.`,
                        )
                      ) {
                        router.delete(`/admin/stages/${stage.id}`, {
                          preserveScroll: true,
                        });
                      }
                    }}
                  >
                    Remove
                  </Button>
                </div>

                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={stage.active}
                    onChange={(e) =>
                      router.patch(
                        `/admin/stages/${stage.id}`,
                        { active: e.target.checked },
                        { preserveScroll: true },
                      )
                    }
                    aria-label={`${stage.name} visible`}
                  />
                  <span className="text-caption text-ink-soft">Visible</span>
                </label>
              </div>
            ))}
          </div>
        )}

        <form
          onSubmit={add}
          className="border-line/20 flex flex-wrap items-end gap-2 border-t pt-4"
        >
          <div className="min-w-40 flex-1">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Stage name, e.g. Booking the venue"
              required
            />
          </div>
          <div className="min-w-48 flex-1">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One line of context (optional)"
            />
          </div>
          <Button type="submit">Add stage</Button>
        </form>
      </div>
    </Panel>
  );
}
